import React, { FC, useState, useEffect, useCallback } from 'react';
import { useTranslation, Trans } from 'react-i18next';
import {
  Button,
  ButtonSize,
  ButtonColor,
  Table,
  TableRow,
  Option,
  Combobox,
} from '@code/innovance-react-ui-kit';
import { useForm, FieldError } from 'react-hook-form';
import {
  FormRow,
  FormElement,
  LineFormElement,
  Title,
  Subtitle,
  Separator,
  Container,
  TableWrapper,
} from 'src/front/components/FormStyles';
import useYupResolver from 'src/front/common/hooks/useYupResolver';
import { AccountsSchema } from 'src/front/common/utils/validationSchemas';
import { PlasticCardAccountsSearchData } from 'src/front/api/generated';
import { PlasticCardRegistrationApi } from 'src/front/api/apiInstance';
import AccountsPeriod from './AccountsPeriod';

interface Props {
  setShowAccounts: (showOperationPerformed: boolean) => void;
  pointOfSaleData: Option[];
}
export interface AccountsData {
  pointOfSale: Option | null;
}

const defaultValues = {
  pointOfSale: null,
};

const tableHeaders = [
  'plasticCardRegistration_documentNumber_label',
  'general_components_accountName_label',
];

const Accounts: FC<Props> = ({ setShowAccounts, pointOfSaleData }) => {
  const initialValue = {
    documentNumber: '',
    accountName: '',
  };
  const { t } = useTranslation();
  const [tableLoading, setTableLoading] = useState<boolean>(false);
  const [showResult, setShowResult] = useState<boolean>(false);
  const [page, setPage] = useState<number>(1);
  const [hasNextPage, setHasNextPage] = useState<boolean>(false);
  const [accountsTableData, setAccountsTableData] = useState<PlasticCardAccountsSearchData[]>([]);
  const [selectedAccountsRow, setSelectedAccountsRow] = useState<TableRow>();
  const [selectedRowData, setSelectedRowData] = useState<PlasticCardAccountsSearchData>(
    initialValue
  );
  const resolver = useYupResolver(AccountsSchema);

  const { watch, register, errors, setValue, handleSubmit, getValues } = useForm<AccountsData>({
    defaultValues: {
      ...defaultValues,
    },
    resolver,
  });

  const onSubmit = async (): Promise<void> => {
    loadOperationList(1, true);
    setShowResult(true);
  };

  const loadOperationList = useCallback(
    async (pageNumber = page, forceRefresh = false) => {
      setTableLoading(true);
      const { pointOfSale } = getValues();
      const { data } = await PlasticCardRegistrationApi.getPlasticCardAccountList({
        pointOfSale: pointOfSale?.value || '',
        refresh: !pageNumber || forceRefresh,
        rowCountPerPage: 10,
        pageNumber,
      });
      if (data.result) {
        setAccountsTableData(data.accountList);
        setHasNextPage(data.hasNextPage);
        setPage(pageNumber || 1);
      }
      setTableLoading(false);
    },
    [getValues, page]
  );

  const goToBack = (): void => setShowAccounts(false);

  const tableList: TableRow[] = accountsTableData.map((item, index) => ({
    id: index.toString(),
    columns: [item.documentNumber, item.accountName],
  }));

  const handleComboChange = (fieldName: string) => async (option: Option | null): Promise<void> => {
    setValue(fieldName, option, { shouldValidate: true, shouldDirty: true });
    setSelectedAccountsRow(undefined);
  };

  const selectedTableItem = (selectedRow: TableRow | undefined): void => {
    accountsTableData.forEach((item, index) => {
      if (index.toString() === selectedRow?.id) {
        setSelectedRowData(item);
      }
    });

    setSelectedAccountsRow(selectedRow);
  };

  const registerFields = useCallback(() => {
    register({ name: 'pointOfSale' });
  }, [register]);

  useEffect(() => {
    registerFields();
  }, [registerFields]);

  return (
    <Container>
      <Title>
        <Trans i18nKey="plasticCardRegistration_title" />
      </Title>
      <Separator />
      <Subtitle>
        <Trans i18nKey="plasticCardRegistration_accounts_subtitle" />
      </Subtitle>
      <form onSubmit={handleSubmit(onSubmit)}>
        <FormRow>
          <LineFormElement>
            <Combobox
              errorMessage={t((errors?.pointOfSale as FieldError)?.message as string)}
              items={pointOfSaleData}
              label={t('plasticCardRegistration_pointOfSale_label')}
              name="pointOfSale"
              onChange={handleComboChange('pointOfSale')}
              value={watch('pointOfSale')}
              required
            />
          </LineFormElement>
        </FormRow>
        <Separator />
        <FormRow>
          <Button
            color={ButtonColor.Primary}
            size={ButtonSize.Medium}
            text={t('general_components_search_button')}
            type="submit"
          />
        </FormRow>
      </form>

      {showResult ? (
        <>
          <Separator />
          <Subtitle>
            <Trans i18nKey="general_components_accountList_text" />
          </Subtitle>
          <Separator />
          <TableWrapper>
            <Table
              emptyTableText={t('general_components_notFound_errorMessage')}
              hasNextPage={hasNextPage}
              headers={tableHeaders.map((head) => t(head))}
              items={tableList}
              loading={tableLoading}
              loadPageItems={loadOperationList}
              onSelect={selectedTableItem}
              pageNumber={page}
              selectedItem={selectedAccountsRow}
              clearItemOnPageChange
            />
          </TableWrapper>

          <Separator />
          {selectedAccountsRow ? <AccountsPeriod selectedAccountsRow={selectedRowData} /> : null}
        </>
      ) : null}
      <Separator />
      <FormElement>
        <Button
          color={ButtonColor.Secondary}
          onClick={goToBack}
          size={ButtonSize.Medium}
          text={t('plasticCardRegistration_back_button')}
          type="button"
        />
      </FormElement>
    </Container>
  );
};

export default Accounts;
